/** One element declaration the served schema carries for a field, generated from the catalog. */
export type E2bEntry = {
  profile: string;
  element: string;
  conformance: "mandatory" | "conditional" | "optional";
  null_flavor: string | null;
};

function rawE2b(schema: unknown): unknown {
  return schema && typeof schema === "object" ? (schema as Record<string, unknown>)["x-e2b"] : undefined;
}

/**
 * The E2B declarations a field's schema carries. The transitional Boolean form (a configured
 * field not yet declared against the catalog) has no per-element detail, so it yields no entries.
 */
export function e2bEntries(schema: unknown): E2bEntry[] {
  const value = rawE2b(schema);
  return Array.isArray(value) ? value as E2bEntry[] : [];
}

/**
 * Whether an object schema's properties declare any E2B elements. `seriousness_criteria` and
 * similar object fields carry no `x-e2b` themselves — the declarations sit on
 * `properties.<name>` — so the object field's own row still needs to show the marker.
 */
export function hasE2bProperties(schema: unknown): boolean {
  if (!schema || typeof schema !== "object") return false;
  const properties = (schema as Record<string, unknown>)["properties"];
  if (!properties || typeof properties !== "object") return false;
  return Object.values(properties as Record<string, unknown>).some(
    (propertySchema) => e2bEntries(propertySchema).length > 0,
  );
}

/** Whether a field is reported in an E2B submission at all — declared entries, the transitional Boolean, or a declared property. */
export function isE2bField(schema: unknown): boolean {
  return e2bEntries(schema).length > 0 || rawE2b(schema) === true || hasE2bProperties(schema);
}

function isEmpty(value: unknown): boolean {
  return value == null || typeof value === "string" && value.trim() === "";
}

/**
 * Whether a field is mandatory under at least one entry that applies on this installation, has no
 * null-flavor escape, and still holds no value. An entry applies when its profile is one of the
 * installation's `declarationProfiles`: `["ICH"]` on ICH, `["ICH", "FDA"]` on FDA.
 */
export function isE2bRequiredAndEmpty(schema: unknown, value: unknown, declarationProfiles: readonly string[]): boolean {
  if (!isEmpty(value)) return false;
  return e2bEntries(schema).some((entry) =>
    entry.conformance === "mandatory" && entry.null_flavor == null
    && declarationProfiles.includes(entry.profile));
}
